import Vue from 'vue';

import { RequiredByRefCount } from '../ref-counter';
import {
  SERVICE_DEFINITIONS_FETCH,
  SERVICE_SELECTED_ADD,
  SERVICE_SELECTED_ADD_QUANTITY,
  SERVICE_SELECTED_ITEM_DELETE,
  SERVICE_SELECTED_JOB_ITEMS_ADD,
  SERVICE_SELECTED_REFRESH,
  SERVICE_SELECTED_SPECIFY,
  SERVICE_SELECTED_ITEM_UNREF,
  CUSTOM_SERVICE_PUSH_ERROR,
  CUSTOM_SERVICE_RESET_ERRORS,
  SERVICE_PRESELECT_AUTOMATION,
  SERVICE_PRESELECT_AUTOMATION_BY_PRODUCT,
  SERVICE_CHANGE_QUANTITY,
} from './types';

import {
  onPartialSelectionChange,
  markUnMarkLinkedServicesOnProduct,
} from '../../utils/partial_selection';

const normalizeNewServiceEntry = (payload, index) => {
  // Create link key
  if (payload.service_tag_link_key === true) {
    payload.service_tag_link_key = `${payload.service}-${index}`;
  }
  // Make sure quantity is number
  if (typeof payload.quantity !== 'number') {
    payload.quantity = parseFloat(payload.quantity);
  }
};

const setLinkedServicesQuantity = (payload, item) => {
  const serviceDefintions = payload?.serviceDefinitions;
  const parentServiceDefinition: any =
    serviceDefintions[payload?.newService?.service];
  const currentServiceDefinition: any = serviceDefintions[item?.service];
  const childService = parentServiceDefinition?.only_in_combination_with?.find(
    (el) => el?.service === item?.service,
  );
  const newQuantity =
    parseFloat(childService.quantity) * parseFloat(payload.newService.quantity);
  return {
    ...item,
    quantity: currentServiceDefinition?.one_or_more === 'one' ? 1 : newQuantity,
  };
};

function deleteAtIndexes(serviceEntries, indexesToDelete, serviceDefinitions) {
  let newServiceEntries: any[] = [...serviceEntries];

  while (indexesToDelete?.length > 0) {
    // Get service ids to be deleted
    const unrefServiceIdList: any[] = indexesToDelete.map((index) => ({
      service: serviceEntries[index].service,
      productId: serviceEntries[index].productId,
    }));

    if (serviceDefinitions) {
      for (const index of indexesToDelete) {
        const serviceEntry = serviceEntries[index];
        const serviceId = serviceEntry.service;
        const parentQuantity = serviceEntry.quantity;

        if (serviceDefinitions[serviceId]?.only_in_combination_with) {
          for (const dependency of serviceDefinitions[serviceId]
            .only_in_combination_with) {
            const childService = newServiceEntries.find(
              (entry) => entry.service === dependency.service,
            );
            if (childService) {
              // Adjust child service quantity based on parent quantity and ratio
              const adjustmentQuantity = dependency.quantity * parentQuantity;
              childService.quantity -= adjustmentQuantity;

              // If child quantity reaches zero, remove the child service
              if (childService.quantity <= 0) {
                childService.quantity = 0; // Safeguard against negative quantity
                indexesToDelete.push(newServiceEntries.indexOf(childService));
              }
            }
          }
        }
      }
    }

    newServiceEntries = newServiceEntries.filter(
      (el, index) => !indexesToDelete.includes(index),
    );

    // Unref
    for (const serviceEntry of newServiceEntries) {
      for (const unrefService of unrefServiceIdList) {
        for (const unrefKeyName of Object.keys(unrefService)) {
          if (
            unrefService[unrefKeyName] &&
            serviceEntry.requiredBy?.has(unrefService[unrefKeyName])
          ) {
            serviceEntry.requiredBy.unrefBy(unrefService[unrefKeyName]);
          }
        }
      }
    }

    // Mark for deletion
    indexesToDelete = [];
    for (let index = 0; index < newServiceEntries.length; index++) {
      if (newServiceEntries[index].requiredBy?.shouldGc()) {
        indexesToDelete.push(index);
      }
    }
  }
  return newServiceEntries;
}

export default {
  [SERVICE_DEFINITIONS_FETCH](state, payload) {
    const opportunity = payload[0].opportunity;
    const services = payload.map((service) => {
      return {
        [service.sfid]: service,
      };
    });
    const serializedServices = Object.assign({}, ...services);
    const serviceDefinitions = {
      [opportunity]: serializedServices,
    };
    state.definitions = Object.assign(
      {},
      state.definitions,
      serviceDefinitions,
    );
  },
  [SERVICE_SELECTED_ADD](state, payload) {
    let previousServices = [];
    if (state.selected[payload.jobId]) {
      previousServices = state.selected[payload.jobId];
    }
    if (!state.selected[payload.jobId]) {
      Vue.set(state.selected, payload.jobId, {});
    }
    normalizeNewServiceEntry(payload.newService, previousServices.length);
    Vue.set(state.selected, payload.jobId, [
      ...previousServices,
      payload.newService,
    ]);
  },
  [SERVICE_SELECTED_SPECIFY](state, payload) {
    const affectedService = state.selected[payload.jobId][payload.serviceIndex];
    normalizeNewServiceEntry(payload.newService, payload.serviceIndex);
    let requiredBy = new RequiredByRefCount();
    if (affectedService.requiredBy) {
      requiredBy.update(affectedService.requiredBy);
    }
    if (payload.newService.requiredBy) {
      requiredBy.update(payload.newService.requiredBy);
    }
    const requiredByUpdate = requiredBy.isEmpty() ? {} : { requiredBy };

    const base = payload.isNew ? {} : affectedService;

    Vue.set(
      state.selected[payload.jobId],
      payload.serviceIndex,
      Object.assign(base, payload.newService, requiredByUpdate),
    );
  },
  [SERVICE_PRESELECT_AUTOMATION](state, payload) {
    const affectedService = state.selected[payload.jobId][payload.serviceIndex];
    if (!affectedService) {
      console.error(
        'using SERVICE_PRESELECT_AUTOMATION with invalid payload',
        payload,
      );
      return;
    }
    const updatedSelections = onPartialSelectionChange(
      affectedService.selections,
      payload,
      'service',
    );
    Vue.set(affectedService, 'selections', updatedSelections);
  },
  [SERVICE_PRESELECT_AUTOMATION_BY_PRODUCT](state, payload) {
    const { jobId, productId, selected } = payload;
    let servicesList = state.selected[jobId];
    const hasAffectedServices = servicesList.some(
      (service) => service.productId === productId,
    );
    if (hasAffectedServices) {
      const updatedServices = markUnMarkLinkedServicesOnProduct(
        servicesList,
        productId,
        jobId,
        selected,
      );
      Vue.set(state.selected, jobId, updatedServices);
    }
  },
  [SERVICE_SELECTED_ADD_QUANTITY](state, payload) {
    const affectedService = state.selected[payload.jobId][payload.serviceIndex];
    if (!affectedService) {
      console.error(
        'using SERVICE_SELECTED_ADD_QUANTITY with invalid payload',
        payload,
      );
      return;
    }
    Vue.set(
      state.selected[payload.jobId][payload.serviceIndex],
      'quantity',
      affectedService.quantity + payload.quantity,
    );
  },
  [SERVICE_CHANGE_QUANTITY](state, payload) {
    const serviceDef =
      payload?.serviceDefinitions[payload?.newService?.service];
    const affectedServices = state.selected[payload.jobId];
    if (serviceDef) {
      if (
        serviceDef.minimum_quantity !== undefined &&
        payload.newService.quantity < serviceDef.minimum_quantity
      ) {
        payload.newService.quantity = serviceDef.minimum_quantity;
      }
      if (
        serviceDef.maximum_quantity &&
        payload.newService.quantity > serviceDef.maximum_quantity
      ) {
        payload.newService.quantity = serviceDef.maximum_quantity;
      }
    }
    affectedServices.map((item, index) => {
      if (
        item.service === payload.newService.service &&
        index === payload.elementIndex
      ) {
        Vue.set(state.selected[payload.jobId], index, {
          ...item,
          quantity:
            item?.one_or_more === 'one'
              ? 1
              : parseFloat(payload.newService.quantity),
        });
      } else if (
        item?.requiredBy?.counter[payload.newService.service] ||
        serviceDef.only_in_combination_with.find(
          (el) => el.service === item.service,
        )
      ) {
        Vue.set(
          state.selected[payload.jobId],
          index,
          setLinkedServicesQuantity(payload, item),
        );
      }
    });
  },
  [SERVICE_SELECTED_REFRESH](state, payload) {
    if (payload.productId) {
      const services = state.selected[payload.jobId];
      if (services?.length) {
        const indexesToDelete: number[] = [];
        services.forEach((service, index) => {
          if (service.productId == payload.productId) {
            indexesToDelete.push(index);
          }
        });
        const newServices = deleteAtIndexes(
          state.selected[payload.jobId],
          indexesToDelete,
          null,
        );
        Vue.set(state.selected, payload.jobId, newServices);
      }
    } else {
      Vue.delete(state.selected, payload.jobId);
    }
  },
  [SERVICE_SELECTED_ITEM_DELETE](state, payload) {
    if (state.selected[payload.jobId] && payload?.serviceIndex >= 0) {
      const newServices = deleteAtIndexes(
        state.selected[payload.jobId],
        [payload.serviceIndex],
        payload.serviceDefinitions,
      );
      Vue.set(state.selected, payload.jobId, newServices);
    }
  },
  [SERVICE_SELECTED_JOB_ITEMS_ADD](state, payload) {
    state.selected = {
      ...{},
      ...state.selected,
      ...payload,
    };
  },
  [SERVICE_SELECTED_ITEM_UNREF](state, payload) {
    const affectedService = state.selected[payload.jobId][payload.serviceIndex];
    const update: any = {
      quantity: affectedService.quantity,
    };
    if (payload.quantityDelta) {
      update.quantity = update.quantity + payload.quantityDelta;
    }
    if (update.quantity > 0) {
      Vue.set(
        state.selected[payload.jobId],
        payload.serviceIndex,
        Object.assign(affectedService, update),
      );
    } else {
      const newServices = deleteAtIndexes(
        state.selected[payload.jobId],
        [payload.serviceIndex],
        null,
      );
      Vue.set(state.selected, payload.jobId, newServices);
    }
  },
  [CUSTOM_SERVICE_PUSH_ERROR](state, { index, errorString }) {
    if (errorString) {
      Vue.set(state.customServicesErrors, index, errorString);
    } else {
      Vue.delete(state.customServicesErrors, index);
    }
  },
  [CUSTOM_SERVICE_RESET_ERRORS](state) {
    state.customServicesErrors = {};
  },
};
